/**
 * Lab `highStock.js` `createChart` options — dark stock chart, All zoom, dual rates + bets.
 */

import type { Options } from 'highcharts'
import type { LabRateTick } from './fetchRunnerRates'
import {
  betsToHighstockScatter,
  labTicksToHighstockRates,
} from './highstockSeries'
import { formatSurveillanceChartTitle } from './surveillanceLabels'
import type { SurveillanceBet, SurveillanceMarketMeta } from './types'

const LABEL = { color: '#fff', fontSize: '14px' } as const
const LABEL_X = { color: '#fff', fontSize: '12px' } as const

const RATE_BACK_TIP =
  '<span style="background:white;color:{series.color}">{series.name}</span>: <b>{point.y}</b><br/>' +
  '<span style="background:white;color:{series.color}">Back Volume </span>: <b>{point.rateObj.bs}</b>'
const RATE_LAY_TIP =
  '<span style="background:white;color:{series.color}">{series.name}</span>: <b>{point.y}</b><br/>' +
  '<span style="background:white;color:{series.color}">Lay Volume </span>: <b>{point.rateObj.ls}</b>'
const BET_TIP =
  '<span style="background:white;color:{series.color}">{series.name}</span>: <b>{point.y}</b><br/>' +
  'Username: <b>{point.bet.u}</b><br/>stake: <b>{point.bet.s}</b><br/>IP: <b>{point.bet.IP}</b><br/>'

/** Lab Highcharts hamburger — exporting + export-data menu (Winner7). */
export const SURVEILLANCE_EXPORT_MENU_ITEMS = [
  'viewFullscreen',
  'printChart',
  'separator',
  'downloadPNG',
  'downloadJPEG',
  'downloadPDF',
  'downloadSVG',
  'separator',
  'downloadCSV',
  'downloadXLS',
  'viewData',
] as const

export function buildHighstockOptions(input: {
  market: SurveillanceMarketMeta
  runnerName: string
  ticks: LabRateTick[]
  bets: SurveillanceBet[]
}): Options {
  const { backRate, layRate } = labTicksToHighstockRates(input.ticks)
  const { backBets, layBets } = betsToHighstockScatter(input.bets)
  const title = formatSurveillanceChartTitle([
    input.market.c,
    input.market.e,
    input.market.m,
    input.runnerName,
  ])

  return {
    title: {
      text: title,
      style: { color: '#fff' },
    },
    chart: {
      height: 600,
      backgroundColor: '#0b0b0b',
    },
    credits: { enabled: false },
    exporting: {
      enabled: true,
      fallbackToExportServer: false,
      filename: title.replace(/[^\w.-]+/g, '_').slice(0, 80) || 'surveillance-chart',
      buttons: {
        contextButton: {
          menuItems: [...SURVEILLANCE_EXPORT_MENU_ITEMS],
          theme: {
            fill: '#1a1a1a',
            stroke: '#555',
          },
          symbolStroke: '#fff',
          symbolFill: '#fff',
        },
      },
      csv: {
        dateFormat: '%Y-%m-%d %H:%M:%S',
      },
    },
    navigation: {
      buttonOptions: {
        verticalAlign: 'top',
        y: 0,
      },
      menuStyle: {
        background: '#1a1a1a',
        border: '1px solid #444',
        borderRadius: '4px',
        padding: '4px 0',
      },
      menuItemStyle: {
        color: '#eee',
        background: 'none',
        fontSize: '13px',
        padding: '8px 14px',
      },
      menuItemHoverStyle: {
        background: '#333',
        color: '#fff',
      },
    },
    rangeSelector: {
      buttons: [{ type: 'all', text: 'All' }],
      selected: 0,
      inputEnabled: false,
    },
    navigator: {
      enabled: true,
      maskFill: 'rgba(102, 133, 194, 0.3)',
    },
    scrollbar: { enabled: true },
    yAxis: {
      gridLineColor: '#333',
      labels: {
        style: LABEL,
      },
      plotLines: [{ value: 0, width: 1, color: 'silver' }],
    },
    xAxis: {
      gridLineColor: '#222',
      labels: { style: LABEL_X },
    },
    legend: { enabled: false },
    tooltip: {
      valueDecimals: 2,
      split: true,
      pointFormat:
        '<span style="background:white;color:{series.color}">{series.name}</span>: <b>{point.y}</b><br/>',
    },
    time: { timezone: 'UTC' },
    series: [
      {
        type: 'line',
        name: 'Back Rate',
        data: backRate,
        color: '#87A6F3',
        turboThreshold: 1_000_000,
        tooltip: { pointFormat: RATE_BACK_TIP, valueDecimals: 2 },
      },
      {
        type: 'line',
        name: 'Lay Rate',
        data: layRate,
        color: '#FDB2A2',
        turboThreshold: 1_000_000,
        tooltip: { pointFormat: RATE_LAY_TIP, valueDecimals: 2 },
      },
      {
        type: 'scatter',
        name: 'Lay Bet',
        data: layBets,
        color: '#FF5733',
        lineWidth: 0,
        turboThreshold: 1_000_000,
        marker: {
          enabled: true,
          radius: 5,
          fillColor: '#FF5733',
          symbol: 'circle',
        },
        tooltip: { pointFormat: BET_TIP, valueDecimals: 2 },
        states: { hover: { lineWidthPlus: 0 } },
      },
      {
        type: 'scatter',
        name: 'Back Bet',
        data: backBets,
        color: '#1756F3',
        lineWidth: 0,
        turboThreshold: 1_000_000,
        marker: {
          enabled: true,
          radius: 5,
          fillColor: '#1756F3',
          symbol: 'circle',
        },
        tooltip: { pointFormat: BET_TIP, valueDecimals: 2 },
        states: { hover: { lineWidthPlus: 0 } },
      },
    ],
  }
}
